// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, Users, Star } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import RegistrationDetailDrawer from "./RegistrationDetailDrawer";
import EventSheetTabs from "@/components/events/EventSheetTabs";
import { parseLocalDate } from '@/utils/formatters';


const REG_STATUS_FILTERS = [
  { value: "all", label: "All registrations" },
  { value: "registered", label: "Registered" },
  { value: "checked_in", label: "Checked in" },
  { value: "completed", label: "Completed" },
  { value: "waitlisted", label: "Waitlisted" },
  { value: "dropped", label: "Dropped" },
  { value: "refunded", label: "Refunded" },
];

const PAY_STATUS_FILTERS = [
  { value: "all", label: "All payments" },
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
  { value: "partial", label: "Partial" },
  { value: "refunded", label: "Refunded" },
];

const REG_BADGE: Record<string, string> = {
  registered: "bg-blue-100 text-blue-800",
  checked_in: "bg-purple-100 text-purple-800",
  completed: "bg-green-100 text-green-800",
  waitlisted: "bg-amber-100 text-amber-800",
  dropped: "bg-red-100 text-red-800",
  refunded: "bg-red-100 text-red-800",
  draft: "bg-muted text-muted-foreground",
  pending_payment: "bg-blue-100 text-blue-800", // legacy rows
};

const PAY_BADGE: Record<string, string> = {
  paid: "bg-green-100 text-green-800",
  unpaid: "bg-amber-100 text-amber-800",
  partial: "bg-amber-100 text-amber-800",
  refunded: "bg-red-100 text-red-800",
};

const prettyReg = (s?: string) =>
  (s || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function EventTeamsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetMode, setSheetMode] = useState(
    () => searchParams.get("view") === "spreadsheet"
  );
  const [search, setSearch] = useState("");

  const [divisionFilter, setDivisionFilter] = useState("all");
  const [poolFilter, setPoolFilter] = useState("all");
  const [regStatusFilter, setRegStatusFilter] = useState("all");
  const [payStatusFilter, setPayStatusFilter] = useState("all");
  const [openRegistrationId, setOpenRegistrationId] = useState<string | null>(null);

  const { data: event } = useQuery({
    queryKey: ["event-teams-event", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("events")
        .select(
          "id, name, slug, start_date, end_date, registration_levels_enabled"
        )
        .eq("id", eventId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const levelsEnabled = !!event?.registration_levels_enabled;

  const { data: levels = [] } = useQuery({
    queryKey: ["event-registration-levels", eventId],
    enabled: !!eventId && levelsEnabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("event_registration_levels")
        .select("id, label, sort_order")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: registrations = [], isLoading, error: registrationsError } = useQuery({
    queryKey: ["event-teams", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data: regs, error } = await (supabase as any).rpc(
        "get_event_registered_teams",
        { _event_id: eventId }
      );
      if (error) throw error;

      // Starred teams — "the ones whose games have to be really good". The
      // star lives on the registration, so it is per event, and the placement
      // wave "Place top teams" scopes to games either starred team is in.
      const { data: flags } = await (supabase as any)
        .from("event_registrations_v2")
        .select("id, is_priority")
        .eq("event_id", eventId);
      const priority = new Map((flags || []).map((f: any) => [f.id, !!f.is_priority]));

      return (regs || []).map((r: any) => ({
        ...r,
        is_priority: priority.get(r.id) ?? false,
        teams: {
          display_name: r.team_display_name,
          canonical_name: r.team_canonical_name,
          primary_coach_name: r.primary_coach_name,
        },
      }));
    },
  });

  const handleTogglePriority = async (registrationId: string, next: boolean) => {
    const { error } = await (supabase as any)
      .from("event_registrations_v2")
      .update({ is_priority: next })
      .eq("id", registrationId);
    if (error) {
      toast.error("Could not change the star");
      return;
    }
    toast.success(next ? "Starred — a top team for this event" : "Star removed");
    queryClient.invalidateQueries({ queryKey: ["event-teams", eventId] });
  };

  const handlePoolChange = async (registrationId: string, newPool: string) => {
    const { error } = await (supabase as any)
      .from("event_registrations_v2")
      .update({ pool: newPool || null })
      .eq("id", registrationId);
    if (error) {
      toast.error("Failed to update pool");
      return;
    }
    toast.success("Pool updated");
    queryClient.invalidateQueries({ queryKey: ["event-teams", eventId] });
  };

  const divisions = useMemo(() => {
    const set = new Set<string>();
    // age_group is the populated field; the legacy `division` column is empty.
    registrations.forEach((r: any) => r.age_group && set.add(r.age_group));
    return Array.from(set).sort();
  }, [registrations]);

  const filtered = useMemo(() => {
    return registrations.filter((r: any) => {
      const name =
        r.display_name_override ||
        r.teams?.display_name ||
        r.teams?.canonical_name ||
        "";
      if (search && !name.toLowerCase().includes(search.toLowerCase()))
        return false;
      if (divisionFilter !== "all" && r.age_group !== divisionFilter) return false;
      if (levelsEnabled && poolFilter !== "all" && (r.pool || "") !== poolFilter)
        return false;
      const rs = (r.registration_status || "").toLowerCase();
      const ps = (r.payment_status || "").toLowerCase();
      if (regStatusFilter !== "all" && rs !== regStatusFilter) return false;
      if (payStatusFilter !== "all" && ps !== payStatusFilter) return false;
      return true;
    });
  }, [registrations, search, divisionFilter, poolFilter, regStatusFilter, payStatusFilter, levelsEnabled]);

  const counts = useMemo(() => {
    const c = { total: registrations.length, paid: 0, unpaid: 0, registered: 0 };
    registrations.forEach((r: any) => {
      const rs = (r.registration_status || "").toLowerCase();
      const ps = (r.payment_status || "").toLowerCase();
      if (rs === "registered" || rs === "checked_in" || rs === "completed" || rs === "waitlisted") c.registered++;
      if (ps === "paid") c.paid++;
      else c.unpaid++;
    });
    return c;
  }, [registrations]);

  const eventDates =
    event?.start_date && event?.end_date
      ? `${format(parseLocalDate(event.start_date), "MMM d")} – ${format(
          parseLocalDate(event.end_date),
          "MMM d, yyyy"
        )}`
      : "";

  return (
    <div className="space-y-6 px-4 pt-6 md:px-6 md:pt-10 lg:px-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            onClick={() => navigate(`/admin-dashboard/events/${eventId}`)}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to event overview
          </button>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <Users className="w-6 h-6" />
            Registered Teams
          </h1>
          <p className="text-muted-foreground">
            {event?.name} {eventDates && <span>· {eventDates}</span>}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {counts.total} teams · {counts.registered} registered · {counts.paid} paid · {counts.unpaid} unpaid
          </p>
        </div>
        <Button
          variant={sheetMode ? "default" : "outline"}
          onClick={() => {
            const next = !sheetMode;
            setSheetMode(next);
            const p = new URLSearchParams(searchParams);
            if (next) p.set("view", "spreadsheet");
            else p.delete("view");
            setSearchParams(p, { replace: true });
          }}
        >
          {sheetMode ? "Table view" : "Spreadsheet view"}
        </Button>
      </div>

      {sheetMode ? (
        <div className="h-[calc(100vh-220px)]">
          <EventSheetTabs eventId={eventId!} eventName={event?.name} />
        </div>
      ) : (
      <>
      <Card>

        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Input
              placeholder="Search team name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
            <select
              value={divisionFilter}
              onChange={(e) => setDivisionFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="all">All divisions</option>
              {divisions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            {levelsEnabled && (
              <select
                value={poolFilter}
                onChange={(e) => setPoolFilter(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="all">All pools</option>
                {levels.map((l: any) => (
                  <option key={l.id} value={l.label}>
                    {l.label}
                  </option>
                ))}
              </select>
            )}
            <select
              value={regStatusFilter}
              onChange={(e) => setRegStatusFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {REG_STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <select
              value={payStatusFilter}
              onChange={(e) => setPayStatusFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {PAY_STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: 44 }} title="Star your top teams — the placement wave can schedule their games first">
                  <span className="sr-only">Top team</span>
                  <Star className="h-4 w-4" />
                </TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Division</TableHead>
                {levelsEnabled && (
                  <TableHead style={{ width: 130 }}>Pool</TableHead>
                )}
                <TableHead>Status</TableHead>
                <TableHead>Coach</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={levelsEnabled ? 7 : 6} className="text-center text-muted-foreground py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : registrationsError ? (
                <TableRow>
                  <TableCell colSpan={levelsEnabled ? 7 : 6} className="text-center text-muted-foreground py-8">
                    Failed to load registered teams: {(registrationsError as any)?.message || "Unknown error"}
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={levelsEnabled ? 7 : 6} className="text-center text-muted-foreground py-8">
                    No registered teams match your filters.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r: any) => {
                  const teamName =
                    r.display_name_override ||
                    r.teams?.display_name ||
                    r.teams?.canonical_name ||
                    "—";
                  const colSpan = levelsEnabled ? 7 : 6;
                  const isOpen = openRegistrationId === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <TableRow
                        onClick={() =>
                          setOpenRegistrationId(isOpen ? null : r.id)
                        }
                        className={`cursor-pointer hover:bg-muted/50 ${isOpen ? "bg-muted/40" : ""}`}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            aria-label={r.is_priority ? `Unstar ${teamName}` : `Star ${teamName}`}
                            aria-pressed={!!r.is_priority}
                            title="Top team — Place top teams schedules their games first"
                            onClick={(e) => { e.stopPropagation(); handleTogglePriority(r.id, !r.is_priority); }}
                            className="p-1 rounded hover:bg-muted"
                          >
                            <Star className={`h-4 w-4 ${r.is_priority ? "fill-amber-400 text-amber-500" : "text-muted-foreground"}`} />
                          </button>
                        </TableCell>
                        <TableCell className="font-medium">{teamName}</TableCell>
                        <TableCell>{r.age_group || "—"}</TableCell>
                        {levelsEnabled && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <select
                              value={r.pool || ""}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                handlePoolChange(r.id, e.target.value)
                              }
                              className="h-8 rounded-md border border-input bg-background px-2 text-sm w-full"
                            >
                              <option value="">—</option>
                              {levels.map((lvl: any) => (
                                <option key={lvl.id} value={lvl.label}>
                                  {lvl.label}
                                </option>
                              ))}
                            </select>
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex flex-col gap-1 items-start">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs ${REG_BADGE[(r.registration_status || "").toLowerCase()] ?? "bg-muted text-muted-foreground"}`}>
                              {prettyReg(r.registration_status)}
                            </span>
                            {r.payment_status && r.payment_status.toLowerCase() !== "paid" && (
                              <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${PAY_BADGE[r.payment_status.toLowerCase()] ?? "bg-muted text-muted-foreground"}`}>
                                {r.payment_status}
                                {r.payment_method_chosen && r.payment_method_chosen !== "online" ? ` · ${r.payment_method_chosen}` : ""}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{r.teams?.primary_coach_name || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {r.primary_billing_email || "—"}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={colSpan} className="p-4 bg-muted/10">
                            <RegistrationDetailDrawer
                              registrationId={r.id}
                              eventId={eventId!}
                              levelsEnabled={levelsEnabled}
                              levels={levels as any}
                              onClose={() => setOpenRegistrationId(null)}
                              invalidateList={() =>
                                queryClient.invalidateQueries({ queryKey: ["event-teams", eventId] })
                              }
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>
      )}
    </div>

  );
}
